<script setup lang="ts">
import { AssetType, SellingMethod } from '~/apiClient';
import { ROUTE_NAMES } from '~/constants/routes';

// COMPOSABLES
const { tt } = useTypedI18n();
useSeoMeta({
    title: tt('seo.howSellUsedBus.title'),
    ogTitle: tt('seo.howSellUsedBus.title'),
    description: tt('seo.howSellUsedBus.description'),
    ogDescription: tt('seo.howSellUsedBus.description'),
});
const { formatSlpRoute } = useSlpRouteUtils();
const { locale } = useI18n();
const localePath = useLocalePath();

// HANDLES
const onHeroCtaClick = (): void => {
    scrollIntoView('reach');
};
</script>

<template>
    <div class="mb-12 flex flex-col gap-y-12 sm:mb-16 sm:gap-y-16 xl:mb-28 xl:gap-y-28">
        <BlockHeroLinedUp
            :title="tt('howSellUsedBus.hero.title')"
            :media="{ type: 'image', src: '/how-sell-used-bus/hero.webp' }"
            :button="{ trailingIcon: 'lucide:move-down', label: tt('howSellUsedBus.hero.cta') }"
            @cta-click="onHeroCtaClick"
        >
            <template #description>
                <div class="max-w-[960px]">
                    <i18n-t keypath="howSellUsedBus.hero.description1.base" tag="p" scope="global" class="text-muted pb-8 text-lg font-medium">
                        <template #bold>
                            <span class="font-bold"> {{ tt('howSellUsedBus.hero.description1.bold') }} </span>
                        </template>
                    </i18n-t>
                    <i18n-t keypath="howSellUsedBus.hero.description2.base" tag="p" scope="global" class="text-muted text-lg font-medium">
                        <template #bold1>
                            <span class="font-bold"> {{ tt('howSellUsedBus.hero.description2.bold1') }} </span>
                        </template>
                        <template #bold2>
                            <span class="font-bold"> {{ tt('howSellUsedBus.hero.description2.bold2') }} </span>
                        </template>
                    </i18n-t>
                </div>
            </template>
        </BlockHeroLinedUp>
        <BlockSectionFullWidth id="reach" :title="tt('howSellUsedBus.sectionReach.title')" variant="gradient">
            <template #description>
                <p class="pb-8">{{ tt('howSellUsedBus.sectionReach.description1') }}</p>
                <p>{{ tt('howSellUsedBus.sectionReach.description2') }}</p>
            </template>
        </BlockSectionFullWidth>
        <BlockSectionFullWidth
            :title="tt('howSellUsedBus.sectionEnhance.title')"
            variant="neutral"
            :button="{
                target: '_blank',
                trailingIcon: 'lucide:chevron-right',
                label: tt('howSellUsedBus.sectionEnhance.cta'),
                to: localePath(ROUTE_NAMES.SELL),
            }"
        >
            <template #description>
                <p class="text-default pb-8">{{ tt('howSellUsedBus.sectionEnhance.description1') }}</p>
                <i18n-t keypath="howSellUsedBus.sectionEnhance.description2.base" tag="p" scope="global" class="text-default pb-8">
                    <template #bold>
                        <span class="font-bold"> {{ tt('howSellUsedBus.sectionEnhance.description2.bold') }} </span>
                    </template>
                </i18n-t>
                <p class="text-default">{{ tt('howSellUsedBus.sectionEnhance.description3') }}</p>
            </template>
        </BlockSectionFullWidth>
        <BlockSectionWithImage
            :title="tt('howSellUsedBus.sectionNegotiationTimesAndRisks.title')"
            :image="`/how-sell-used-bus/negotiation-times-and-risks-${locale}.webp`"
            class="max-w-[960px]"
            reverse
        >
            <template #description>
                <p class="text-default pb-8">{{ tt('howSellUsedBus.sectionNegotiationTimesAndRisks.description1') }}</p>
                <p class="text-default pb-8">{{ tt('howSellUsedBus.sectionNegotiationTimesAndRisks.description2') }}</p>
                <p class="text-default">{{ tt('howSellUsedBus.sectionNegotiationTimesAndRisks.description3') }}</p>
            </template>
        </BlockSectionWithImage>
        <BlockSectionWithImage
            :title="tt('howSellUsedBus.sectionPaymentSecurelyAndReliably.title')"
            :image="`/how-sell-used-bus/receive-payment-securely-and-reliably-${locale}.webp`"
            class="max-w-[960px]"
        >
            <template #description>
                <p class="text-default pb-8">{{ tt('howSellUsedBus.sectionPaymentSecurelyAndReliably.description1') }}</p>
                <p class="text-default pb-8 font-bold">{{ tt('howSellUsedBus.sectionPaymentSecurelyAndReliably.description2') }}</p>
                <i18n-t keypath="howSellUsedBus.sectionPaymentSecurelyAndReliably.description3.base" tag="p" scope="global" class="text-default">
                    <template #bold1>
                        <span class="font-bold"> {{ tt('howSellUsedBus.sectionPaymentSecurelyAndReliably.description3.bold1') }} </span>
                    </template>
                    <template #bold2>
                        <span class="font-bold"> {{ tt('howSellUsedBus.sectionPaymentSecurelyAndReliably.description3.bold2') }} </span>
                    </template>
                </i18n-t>
            </template>
        </BlockSectionWithImage>
        <BlockSectionWithImage
            :title="tt('howSellUsedBus.sectionManageDocumentationAndBureaucracyWithoutWorries.title')"
            :image="`/how-sell-used-bus/manage-documentation-and-bureaucracy-without-worries-${locale}.webp`"
            class="max-w-[960px]"
            reverse
        >
            <template #description>
                <p class="text-default pb-8">{{ tt('howSellUsedBus.sectionManageDocumentationAndBureaucracyWithoutWorries.description1') }}</p>
                <p class="text-default pb-8">
                    {{ tt('howSellUsedBus.sectionManageDocumentationAndBureaucracyWithoutWorries.description2') }}
                </p>
                <i18n-t
                    keypath="howSellUsedBus.sectionManageDocumentationAndBureaucracyWithoutWorries.description3.base"
                    tag="p"
                    scope="global"
                    class="text-default"
                >
                    <template #bold>
                        <span class="font-bold">
                            {{ tt('howSellUsedBus.sectionManageDocumentationAndBureaucracyWithoutWorries.description3.bold') }}
                        </span>
                    </template>
                </i18n-t>
            </template>
        </BlockSectionWithImage>
        <BaseWidthContainer class="text-center">
            <UButton
                color="primary"
                size="xl"
                trailing-icon="lucide:chevron-right"
                :label="tt('howSellUsedBus.sellNowCta')"
                :to="localePath(ROUTE_NAMES.SELL)"
                target="_blank"
            />
        </BaseWidthContainer>
        <BlockSection class="max-w-[960px]" :title="tt('howSellUsedBus.whySell.title')" :description="tt('howSellUsedBus.whySell.description')">
            <BlockContentFeatureGrid
                :items="[
                    { title: tt('howSellUsedBus.whySell.items.item1') },
                    { title: tt('howSellUsedBus.whySell.items.item2') },
                    { title: tt('howSellUsedBus.whySell.items.item3') },
                    { title: tt('howSellUsedBus.whySell.items.item4') },
                    { title: tt('howSellUsedBus.whySell.items.item5') },
                    { title: tt('howSellUsedBus.whySell.items.item6') },
                ]"
            />
        </BlockSection>
        <BlockSectionFullWidth
            :button="{
                trailingIcon: 'lucide:chevron-right',
                label: tt('howSellUsedBus.sectionPlatformDesignedToEnhanceYourFleet.cta'),
                to: localePath(ROUTE_NAMES.SELL),
                target: '_blank',
            }"
            :title="tt('howSellUsedBus.sectionPlatformDesignedToEnhanceYourFleet.title')"
            :description="tt('howSellUsedBus.sectionPlatformDesignedToEnhanceYourFleet.description')"
        />
        <BlockSectionFullWidth
            icon="fl:standard"
            :button="{
                trailingIcon: 'lucide:chevron-right',
                label: tt('howSellUsedBus.sectionStandardThatImprovesTheEntireProcess.cta'),
                to: localePath(formatSlpRoute({ sellingMethod: SellingMethod.Auction, assetType: AssetType.Bus })),
            }"
            :title="tt('howSellUsedBus.sectionStandardThatImprovesTheEntireProcess.title')"
            variant="neutral"
        >
            <template #description>
                <i18n-t keypath="howSellUsedBus.sectionStandardThatImprovesTheEntireProcess.description1.base" tag="p" scope="global" class="pb-8">
                    <template #bold1>
                        <span class="font-bold">
                            <i18n-t
                                keypath="howSellUsedBus.sectionStandardThatImprovesTheEntireProcess.description1.bold1"
                                tag="span"
                                scope="global"
                                class="font-bold"
                            >
                                <template #primary>
                                    <span class="text-primary">
                                        {{ tt('howSellUsedBus.sectionStandardThatImprovesTheEntireProcess.description1.primary') }}
                                    </span>
                                </template>
                            </i18n-t>
                        </span>
                    </template>
                    <template #bold2>
                        <span class="font-bold">
                            {{ tt('howSellUsedBus.sectionStandardThatImprovesTheEntireProcess.description1.bold2') }}
                        </span>
                    </template>
                </i18n-t>
                <i18n-t keypath="howSellUsedBus.sectionStandardThatImprovesTheEntireProcess.description2.base" tag="p" scope="global" class="pb-8">
                    <template #primary>
                        <span class="text-primary font-bold">
                            {{ tt('howSellUsedBus.sectionStandardThatImprovesTheEntireProcess.description2.primary') }}
                        </span>
                    </template>
                    <template #bold>
                        <span class="font-bold">
                            {{ tt('howSellUsedBus.sectionStandardThatImprovesTheEntireProcess.description2.bold') }}
                        </span>
                    </template>
                </i18n-t>
            </template>
        </BlockSectionFullWidth>
        <BlockSection :title="tt('howSellUsedBus.faq.title')">
            <BaseAccordion
                size="xl"
                :items="[
                    { label: tt('howSellUsedBus.faq.items.item1.title'), content: tt('howSellUsedBus.faq.items.item1.content') },
                    { label: tt('howSellUsedBus.faq.items.item2.title'), content: tt('howSellUsedBus.faq.items.item2.content') },
                    { label: tt('howSellUsedBus.faq.items.item3.title'), content: tt('howSellUsedBus.faq.items.item3.content') },
                ]"
            />
        </BlockSection>
    </div>
</template>
